/**
 * The optional supplements of ONE bed, as the supplement select's options.
 *
 * Read per bed, never shared between rooms: a supplement sold as a percentage
 * carries the same name on every room type and a value priced off THAT room's
 * night, so an early check-in at 50 % is 24.20 on a 48.40 room and 15.00 on a
 * 30.00 one. Keying the options by name over a booking-wide list quoted — and
 * booked, ids included — the first room's supplement on every other room.
 */
export const toOptionalSupplementOptions = (optionalSupplements) =>
  (optionalSupplements ?? []).map((supplement) => ({
    id: supplement?.id,
    displayName: supplement?.name,
    value: supplement?.name,
    // `value` is the priced surcharge the bed already carries, kept as quoted:
    // a percentage supplement lands on the cents (24.20 on a 48.40 room), and
    // rounding it dropped them from every total it reaches. `|| 0` guards the
    // sums downstream — Number(undefined) is NaN, which poisons them.
    price: Number(supplement?.value ?? 0) || 0,
    priceInDollar: Number(supplement?.priceInDollar ?? 0) || 0,
    kind: supplement?.kind,
    code: supplement?.code,
    // KD-3496 — the rate the slot is sold at, quoted in the extra-nights note;
    // its hours are derived from the code.
    benefitValue: supplement?.benefitValue,
  }));

/**
 * The same supplement as the bed now shown sells it.
 *
 * A selection survives a bed change, but it carries the ids and the amount of
 * the bed it was ticked on. Names are what identifies a supplement across beds
 * (the ids are per bed), so the tick stays visible and the room is priced at
 * the rate the bed being booked is sold at. Falls back to the supplement itself
 * when this bed does not offer it.
 */
export const resolveSupplementOnBed = (options, supplement) =>
  (options ?? []).find(
    (option) => option?.displayName === supplement?.displayName,
  ) ?? supplement;

/**
 * What a room card quotes: the room alone, supplements excluded.
 *
 * A room being chosen or already validated carries its optional supplements
 * folded into `bed.totalPriceWithPromo`; they belong to the booking total in the
 * summary, not to the price of the room itself. `fallbackBed` prices a room that
 * carries no `bed` of its own — an availability possibility, whose bed is the
 * one the card currently displays.
 */
export const roomPriceWithoutSupplements = (room, fallbackBed) => {
  const bed = room?.bed ?? fallbackBed;
  const supplements = (room?.optionalPromos ?? []).reduce(
    (total, { price }) => total + (price ?? 0),
    0,
  );

  return (bed?.totalPriceWithPromo ?? 0) - supplements;
};
